// @ts-nocheck
/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import useAppStore from '../../store';
import { generateSimpleId } from '../../store/utils';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// KONSTANTEN & HELPER
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const ROLES: Record<string, string[]> = {
    'Produktiv (auch prod. Azubi)': ['Friseur', 'Friseur (Führung)', 'Kosmetischer MA', 'Prod. Azubi', 'Sonstiges'],
    'Unproduktiv': ['Reinigung', 'Rezeption', 'Social Media', 'Hausmeister', 'Office', 'Unprod. Anteil', 'Sonstiges'],
    'Azubi & Assistenz': ['Azubi', 'Assistenzkraft'],
    'Austritt': ['Mitarbeiter verlässt Unternehmen'] // 💉 NEU: Austritts-Rolle
};

const getRoleTooltip = (type: string) => {
    if (type === 'Produktiv (auch prod. Azubi)') return 'Fachrichtung wählen (z.B. Friseur)';
    if (type === 'Unproduktiv') return 'Support-Rolle wählen (z.B. Rezeption)';
    if (type === 'Azubi & Assistenz') return 'Azubi oder Assistenz wählen';
    if (type === 'Austritt') return 'Mitarbeiter scheidet aus dem Unternehmen aus';
    return 'Zuerst den Bereich wählen';
};

const isUnprodAnteilEmp = (e: any) => String(e?.taetigkeit || e?.role || '').trim() === 'Unprod. Anteil';

const findLinkedUnprodRows = (prodId: string, prodName: string, unprodList: any[]) =>
    (unprodList || []).filter((u: any) => {
        if (!u || !isUnprodAnteilEmp(u)) return false;
        if (String(u.linkedProductiveEmployeeId || '') === String(prodId)) return true;
        if (
            !u.linkedProductiveEmployeeId &&
            prodName &&
            String(u.name || '').trim() === String(prodName).trim()
        ) {
            return true;
        }
        return false;
    });

const resolveLinkedProdId = (unprod: any, personalData: any[]) => {
    const byId = String(unprod?.linkedProductiveEmployeeId || '').trim();
    if (byId) return byId;
    const name = String(unprod?.name || '').trim();
    if (!name) return '';
    const match = (personalData || []).find((p: any) => p && String(p.name || '').trim() === name);
    return match?.id ? String(match.id) : '';
};

const getDropdownLabel = (e: any) => {
    const name = e?.name || 'Ohne Name';
    if (e?._sourceType === 'Produktiv (auch prod. Azubi)') return `${name} (Produktiv)`;
    if (isUnprodAnteilEmp(e)) return `${name} (Unprod. Anteil)`;
    if (e?._sourceType === 'Unproduktiv') return `${name} (${e.taetigkeit || 'Unproduktiv'})`;
    if (e?._sourceType === 'Azubi & Assistenz') return `${name} (${e.taetigkeit || 'Azubi'})`;
    return name;
};

/** Rest-Gehalt/-Stunden eines produktiven MA nach Abzug verknüpfter Unprod.-Anteile */
const getProdRestValues = (prod: any, unprodList: any[]) => {
    let base = Math.max(0, Number(prod?.grundgehalt) || 0);
    let hours = Math.max(0, Number(prod?.wochenstunden) || 0);
    const linked = findLinkedUnprodRows(String(prod?.id || ''), String(prod?.name || ''), unprodList);
    for (const u of linked) {
        if (u.isSplitDeduction === false) continue;
        base = Math.max(0, base - (Number(u.grundgehalt) || 0));
        hours = Math.max(0, hours - (Number(u.wochenstunden) || 0));
    }
    return { base, hours, linked };
};

const buildDraftInputs = (date: string, emp: any, overrides: Record<string, any> = {}) => {
    const inputs: Record<string, any> = {
        date,
        name: overrides.name ?? emp.name ?? '',
        type: overrides.type ?? emp._sourceType ?? '',
        role: overrides.role ?? emp.taetigkeit ?? '',
        base: overrides.base ?? emp.grundgehalt ?? 0,
        add: overrides.add ?? emp.lohnzusatz ?? 0,
        bonus: overrides.bonus ?? emp.bonus ?? 0,
        hours: overrides.hours ?? emp.wochenstunden ?? 0,
        days: overrides.days ?? emp.arbeitstage ?? 0,
        notes: overrides.notes ?? '',
    };
    const linked =
        overrides.linkedProductiveEmployeeId !== undefined
            ? overrides.linkedProductiveEmployeeId
            : emp.linkedProductiveEmployeeId;
    if (linked) inputs.linkedProductiveEmployeeId = linked;
    if (overrides.isSplitDeduction !== undefined || emp.isSplitDeduction !== undefined) {
        inputs.isSplitDeduction =
            overrides.isSplitDeduction !== undefined ? overrides.isSplitDeduction : emp.isSplitDeduction;
    }
    if (overrides.isNewUnprodAnteil) inputs.isNewUnprodAnteil = true;
    return inputs;
};

/** Baut atomar Produktiv + alle verknüpften Unprod.-Anteile als Draft-Paar. */
const buildPairSeedItems = (
    date: string,
    prod: any,
    unprodList: any[],
    existingDraftIds: Set<string>
): Array<{ id: string; inputs: Record<string, any> }> => {
    const items: Array<{ id: string; inputs: Record<string, any> }> = [];
    if (!prod?.id) return items;
    const rest = getProdRestValues(prod, unprodList);
    if (!existingDraftIds.has(String(prod.id))) {
        items.push({
            id: String(prod.id),
            inputs: buildDraftInputs(date, { ...prod, _sourceType: 'Produktiv (auch prod. Azubi)' }, {
                base: rest.base,
                hours: rest.hours,
            }),
        });
    }
    for (const u of rest.linked) {
        if (!u?.id || existingDraftIds.has(String(u.id))) continue;
        items.push({
            id: String(u.id),
            inputs: buildDraftInputs(date, { ...u, _sourceType: 'Unproduktiv' }, {
                linkedProductiveEmployeeId: String(prod.id),
                isSplitDeduction: u.isSplitDeduction !== false,
            }),
        });
    }
    return items;
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// CSS-STYLES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const TableStyles = () => (
    <style>{`
        .pe-tooltip-wrapper { position: relative; }
        .pe-tooltip-wrapper:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            background-color: #E6C299; color: #000000;
            padding: 8px 12px; border-radius: 4px;
            font-size: 0.75rem; font-weight: 700;
            white-space: normal; min-width: 220px; max-width: 300px;
            z-index: 2147483647 !important; pointer-events: none;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8);
            margin-bottom: 12px; text-align: center;
            border: 2px solid #ffffff; line-height: 1.3;
        }
        .pe-tooltip-wrapper:hover::before {
            content: ''; position: absolute;
            bottom: 100%; left: 50%; margin-left: -6px;
            border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            margin-bottom: 0px; z-index: 2147483647 !important;
        }

        th.col-fixed-date, td.col-fixed-date { min-width: 180px !important; width: 180px !important; }
        th.col-fixed-name, td.col-fixed-name { min-width: 320px !important; }
        th.col-fixed-type, td.col-fixed-type { min-width: 240px !important; }
        th.col-fixed-role, td.col-fixed-role { min-width: 220px !important; }
        th.col-fixed-num,  td.col-fixed-num  { min-width: 140px !important; }
        
        .status-badge-grey { color: #94a3b8; font-size: 0.7rem; letter-spacing: 0.05em; font-weight: 700; }
        .input-error { border: 2px solid #ef4444 !important; background-color: rgba(239, 68, 68, 0.1); }

        /* GHOSTING LOGIC - ALTE WERTE GRAU, NEUE WEISS */
        .table-input.ghost-value { color: #64748b !important; } 
        .table-input.active-value { color: #ffffff !important; font-weight: 800; } 
        .table-select.ghost-value { color: #64748b !important; }
        .table-select.active-value { color: #E6C299 !important; font-weight: 800; }
        
        /* 💉 NEU: AUSTRITTS-STYLES */
        .austritt-type { color: #ef4444 !important; border-color: #991b1b !important; background-color: rgba(69, 10, 10, 0.2) !important; }
        .austritt-disabled { color: #ef4444 !important; opacity: 0.5 !important; }

        /* ADMIN BADGE STYLE */
        .admin-badge { 
            background-color: rgba(6, 78, 59, 0.9); 
            border: 1px solid #10b981; 
            color: #d1fae5; 
            font-size: 0.65rem; 
            padding: 4px 2px; 
            border-radius: 4px; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center;
            line-height: 1.1; 
            white-space: nowrap; 
            cursor: pointer; 
            transition: all 0.2s;
            width: 100%;
            height: 2.8rem;
        }
        .admin-badge:hover {
            background-color: #ef4444;
            border-color: #f87171;
            color: white;
        }
        .admin-badge:hover span { display: none; }
        .admin-badge:hover::after { content: 'UNLOCK'; font-weight: 800; }

        /* Paar: Produktiv + Unprod. Anteil als zusammengehörige Doppelzeile */
        tr.pair-row-prod > td {
            background: rgba(16, 185, 129, 0.06) !important;
            border-top: 2px solid rgba(230, 194, 153, 0.55) !important;
            border-bottom: none !important;
        }
        tr.pair-row-prod > td:first-child {
            box-shadow: inset 3px 0 0 #E6C299;
        }
        tr.pair-row-unprod > td {
            background: rgba(230, 194, 153, 0.07) !important;
            border-top: 1px dashed rgba(230, 194, 153, 0.35) !important;
            border-bottom: 2px solid rgba(230, 194, 153, 0.55) !important;
        }
        tr.pair-row-unprod > td:first-child {
            box-shadow: inset 3px 0 0 #E6C299;
        }
        .pair-type-badge {
            display: flex;
            align-items: center;
            height: 2.5rem;
            padding: 0 0.65rem;
            border-radius: 4px;
            font-size: 0.78rem;
            font-weight: 800;
            letter-spacing: 0.02em;
            white-space: nowrap;
        }
        .pair-type-prod {
            color: #6ee7b7;
            background: rgba(16, 185, 129, 0.12);
            border: 1px solid rgba(16, 185, 129, 0.35);
        }
        .pair-type-unprod {
            color: #E6C299;
            background: rgba(230, 194, 153, 0.12);
            border: 1px solid rgba(230, 194, 153, 0.4);
        }
    `}</style>
);

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 1. AKTIVE ZEILE (Bearbeitbar)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ActiveRowProps {
    employeeId: string;
    onAdminLock: (id: string, isLocked: boolean) => void;
    /** Position innerhalb eines Produktiv/Unprod.-Paares */
    pairRole?: 'prod' | 'unprod' | null;
}

const ActiveStaffRow: React.FC<ActiveRowProps> = ({ employeeId, onAdminLock, pairRole = null }) => {
    const { clientData, settings, updateExistingStaffDraft, seedExistingStaffDrafts, resetStaffDraft, commitDraftState } = useAppStore();
    const draft = clientData.drafts.existingStaff[employeeId];
    
    // Originaldaten finden für Vergleich (Produktiv + Unproduktiv + Azubi)
    const allOriginalEmployees = [
        ...(clientData.personalData || []).map((e: any) => ({ ...e, _sourceType: 'Produktiv (auch prod. Azubi)' })),
        ...(clientData.unproductivePersonalData || []).map((e: any) => ({ ...e, _sourceType: 'Unproduktiv' })),
        ...(clientData.azubiAssistenzData || []).map((e: any) => ({ ...e, _sourceType: 'Azubi & Assistenz' })),
    ];
    const original = allOriginalEmployees.find((p: any) => p && p.id === employeeId) || {};
    const productiveEmployees = (clientData.personalData || []).filter((p: any) => p && p.id);
    const isNewUnprodAnteil = Boolean(draft?.inputs?.isNewUnprodAnteil) || !original?.id;
    
    const currency = settings.country?.currency === 'CHF' ? 'CHF' : 'EUR';

    const fmt = (val: any, decimals: number = 2) => {
        if (val === '' || val === undefined) return '';
        return new Intl.NumberFormat('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val);
    };
    
    // Robuster Parser
    const parse = (val: string) => parseFloat(val.toString().replace(/\./g, '').replace(',', '.')) || 0;

    const [localValues, setLocalValues] = useState<any>({});

    useEffect(() => {
        if (draft) {
            setLocalValues({
                ...draft.inputs,
                base: fmt(draft.inputs.base),
                add: fmt(draft.inputs.add),
                bonus: fmt(draft.inputs.bonus),
                hours: fmt(draft.inputs.hours, 1),
                days: fmt(draft.inputs.days, 0),
            });
        }
    }, [draft]);

    if (!draft) return null; 

    // Ghost-Vergleichswerte: Produktiv zeigt Rest nach Split-Abzug (wie Personaldaten)
    const prodRestOrig =
        (original as any)._sourceType === 'Produktiv (auch prod. Azubi)'
            ? getProdRestValues(original, clientData.unproductivePersonalData || [])
            : null;
    const ghostBase = prodRestOrig ? prodRestOrig.base : (original.grundgehalt || 0);
    const ghostHours = prodRestOrig ? prodRestOrig.hours : (original.wochenstunden || 0);

    // --- HANDLER ---

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select(); 
    };

    const syncLinkedProdDraftByDelta = (wageDelta: number, hoursDelta: number) => {
        const linkId = String(
            localValues.linkedProductiveEmployeeId ||
                original.linkedProductiveEmployeeId ||
                ''
        ).trim();
        if (!linkId) return;
        const linkedProdDraft = clientData.drafts?.existingStaff?.[linkId];
        if (!linkedProdDraft || linkedProdDraft.isLocked) return;
        const nextBase = Math.max(0, (Number(linkedProdDraft.inputs?.base) || 0) - wageDelta);
        const nextHours = Math.max(0, (Number(linkedProdDraft.inputs?.hours) || 0) - hoursDelta);
        updateExistingStaffDraft(linkId, 'base', Math.round(nextBase * 100) / 100);
        updateExistingStaffDraft(linkId, 'hours', Math.round(nextHours * 10) / 10);
    };

    const handleBlur = (field: string) => {
        let val = localValues[field];
        commitDraftState(); // Save to LocalStorage immediately

        if (['base', 'add', 'bonus', 'hours', 'days'].includes(field)) {
            const num = parse(val);
            const prevNum = Number(draft.inputs?.[field]) || 0;

            // Live-Sync: Unprod.-Anteil (Abzug) ↔ offene Produktiv-Zeile
            const isUnprodAnteil =
                (localValues.type === 'Unproduktiv' && localValues.role === 'Unprod. Anteil') ||
                draft.inputs?.role === 'Unprod. Anteil';
            const splitOn =
                localValues.isSplitDeduction !== undefined
                    ? localValues.isSplitDeduction !== false
                    : original.isSplitDeduction !== false;
            if (isUnprodAnteil && splitOn && (field === 'base' || field === 'hours')) {
                const wageDelta = field === 'base' ? num - prevNum : 0;
                const hoursDelta = field === 'hours' ? num - prevNum : 0;
                if (wageDelta !== 0 || hoursDelta !== 0) {
                    syncLinkedProdDraftByDelta(wageDelta, hoursDelta);
                }
            }

            updateExistingStaffDraft(employeeId, field, num);
            const dec = field === 'hours' ? 1 : (field === 'days' ? 0 : 2);
            setLocalValues((prev: any) => ({ ...prev, [field]: fmt(num, dec) }));
        } else {
            updateExistingStaffDraft(employeeId, field, val);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLElement).blur(); 
        }
    };

    const handleChange = (field: string, val: string) => {
        setLocalValues((prev: any) => ({ ...prev, [field]: val }));
    };

    // 💉 CHIRURGIE: Intelligenter Select-Handler inkl. Austritts-Protokoll
    const handleSelectChange = (field: string, val: string) => {
        commitDraftState();
        handleChange(field, val);
        updateExistingStaffDraft(employeeId, field, val);

        // Spezielle Austritts-Logik
        if (field === 'type') {
            if (val === 'Austritt') {
                // 1. Setze Rolle um
                handleChange('role', 'Mitarbeiter verlässt Unternehmen');
                updateExistingStaffDraft(employeeId, 'role', 'Mitarbeiter verlässt Unternehmen');
                
                // 2. Nulle alle Werte
                ['base', 'add', 'bonus', 'hours', 'days'].forEach(f => {
                    const zeroVal = f === 'hours' ? '0,0' : (f === 'days' ? '0' : '0,00');
                    handleChange(f, zeroVal);
                    updateExistingStaffDraft(employeeId, f, 0);
                });
            } else if (localValues.type === 'Austritt') {
                // 3. Fallback: User hat Austritt zurückgenommen -> Originalwerte wiederherstellen
                const restoredRole = original.taetigkeit || ROLES[val]?.[0] || '';
                handleChange('role', restoredRole);
                updateExistingStaffDraft(employeeId, 'role', restoredRole);

                const restOnRestore =
                    (original as any)._sourceType === 'Produktiv (auch prod. Azubi)'
                        ? getProdRestValues(original, clientData.unproductivePersonalData || [])
                        : null;
                const restoreBase = restOnRestore ? restOnRestore.base : (original.grundgehalt || 0);
                const restoreHours = restOnRestore ? restOnRestore.hours : (original.wochenstunden || 0);
                
                handleChange('base', fmt(restoreBase));
                updateExistingStaffDraft(employeeId, 'base', restoreBase);
                
                handleChange('add', fmt(original.lohnzusatz));
                updateExistingStaffDraft(employeeId, 'add', original.lohnzusatz || 0);
                
                handleChange('bonus', fmt(original.bonus));
                updateExistingStaffDraft(employeeId, 'bonus', original.bonus || 0);
                
                handleChange('hours', fmt(restoreHours, 1));
                updateExistingStaffDraft(employeeId, 'hours', restoreHours);
                
                handleChange('days', fmt(original.arbeitstage, 0));
                updateExistingStaffDraft(employeeId, 'days', original.arbeitstage || 0);
            }
        }
    };

    // --- GHOSTING HELPER ---
    const getClass = (field: string, draftVal: any, origVal: any, isNumber = false) => {
        let isDiff = false;
        if (isNumber) {
            const numDraft = typeof draftVal === 'string' ? parse(draftVal) : draftVal;
            isDiff = Math.abs((numDraft || 0) - (origVal || 0)) > 0.01;
        } else {
            isDiff = draftVal !== origVal;
        }
        return isDiff ? 'active-value' : 'ghost-value';
    };

    // --- LOGIK ---
    const roleOptions = ROLES[localValues.type] || [];
    const calcs = draft.calculations;
    const roleTooltip = getRoleTooltip(localValues.type);
    const isAustritt = localValues.type === 'Austritt';
    const isDaysDisabled = isAustritt || localValues.type === 'Unproduktiv' || localValues.type === 'Azubi & Assistenz';
    const isLocked = draft.isLocked; // ADMIN STATUS

    const isUnprodAnteilRow = localValues.type === 'Unproduktiv' && localValues.role === 'Unprod. Anteil';
    const matchedProdByName = isUnprodAnteilRow
        ? productiveEmployees.find(
              (p: any) =>
                  String(p.id) === String(localValues.linkedProductiveEmployeeId || original.linkedProductiveEmployeeId || '') ||
                  (localValues.name && String(p.name).trim() === String(localValues.name).trim())
          )
        : null;
    const effectiveLinkedProdId =
        localValues.linkedProductiveEmployeeId || original.linkedProductiveEmployeeId || matchedProdByName?.id || '';
    const hasLinkedProd = isUnprodAnteilRow && Boolean(effectiveLinkedProdId);
    const isSplitActive =
        hasLinkedProd &&
        (localValues.isSplitDeduction !== undefined
            ? localValues.isSplitDeduction !== false
            : original.isSplitDeduction !== false);

    const getProdReference = () => {
        if (!effectiveLinkedProdId) return { maxWage: Infinity, maxHours: Infinity, prodBaseWage: 0, prodBaseHours: 0 };
        const prod = productiveEmployees.find((p: any) => String(p.id) === String(effectiveLinkedProdId));
        if (!prod) return { maxWage: Infinity, maxHours: Infinity, prodBaseWage: 0, prodBaseHours: 0 };
        // Vertrags-Gesamt = Rohwert aus personalData (nicht Rest)
        const baseWage = Math.max(0, Number(prod.grundgehalt) || 0);
        const baseHours = Math.max(0, Number(prod.wochenstunden) || 0);
        return { maxWage: baseWage, maxHours: baseHours, prodBaseWage: baseWage, prodBaseHours: baseHours };
    };

    const handleToggleSplitCoupling = (nextSplit: boolean) => {
        if (!effectiveLinkedProdId || isLocked || isAustritt) return;
        commitDraftState();
        const curUnprodWage = Math.max(0, parse(String(localValues.base ?? 0)));
        const curUnprodHours = Math.max(0, parse(String(localValues.hours ?? 0)));

        // Produktiv-Draft zeigt Rest: Abzug an → Rest sinkt; Frei → Rest steigt wieder
        const linkedProdDraft = clientData.drafts?.existingStaff?.[effectiveLinkedProdId];
        if (linkedProdDraft && !linkedProdDraft.isLocked) {
            const restWage = Math.max(0, Number(linkedProdDraft.inputs?.base) || 0);
            const restHours = Math.max(0, Number(linkedProdDraft.inputs?.hours) || 0);
            if (!nextSplit && isSplitActive) {
                updateExistingStaffDraft(effectiveLinkedProdId, 'base', Math.round((restWage + curUnprodWage) * 100) / 100);
                updateExistingStaffDraft(effectiveLinkedProdId, 'hours', Math.round((restHours + curUnprodHours) * 10) / 10);
            } else if (nextSplit && !isSplitActive) {
                updateExistingStaffDraft(effectiveLinkedProdId, 'base', Math.max(0, Math.round((restWage - curUnprodWage) * 100) / 100));
                updateExistingStaffDraft(effectiveLinkedProdId, 'hours', Math.max(0, Math.round((restHours - curUnprodHours) * 10) / 10));
            }
        }

        setLocalValues((prev: any) => ({
            ...prev,
            linkedProductiveEmployeeId: effectiveLinkedProdId,
            isSplitDeduction: nextSplit,
        }));
        updateExistingStaffDraft(employeeId, 'linkedProductiveEmployeeId', effectiveLinkedProdId);
        updateExistingStaffDraft(employeeId, 'isSplitDeduction', nextSplit);
    };

    const applySplitWageWithSyncedHours = (targetWage: number) => {
        if (isLocked || isAustritt) return;
        commitDraftState();
        const prevWage = Math.max(0, Number(draft.inputs?.base) || 0);
        const prevHours = Math.max(0, Number(draft.inputs?.hours) || 0);
        if (!isSplitActive) {
            const freeWage = Math.max(0, Math.round(targetWage * 100) / 100);
            setLocalValues((prev: any) => ({ ...prev, base: fmt(freeWage, 2), isSplitDeduction: false }));
            updateExistingStaffDraft(employeeId, 'isSplitDeduction', false);
            updateExistingStaffDraft(employeeId, 'base', freeWage);
            return;
        }
        const { maxWage, maxHours, prodBaseWage, prodBaseHours } = getProdReference();
        const clampedWage = Math.round(Math.min(Math.max(0, targetWage), maxWage) * 100) / 100;
        let nextHours = Math.max(0, parse(String(localValues.hours ?? 0)));
        if (prodBaseWage > 0 && prodBaseHours > 0) {
            const syncedHours = Math.round((prodBaseHours * (clampedWage / prodBaseWage)) * 10) / 10;
            nextHours = Math.min(Math.max(0, syncedHours), maxHours);
        }
        syncLinkedProdDraftByDelta(clampedWage - prevWage, nextHours - prevHours);
        setLocalValues((prev: any) => ({
            ...prev,
            linkedProductiveEmployeeId: effectiveLinkedProdId,
            isSplitDeduction: true,
            base: fmt(clampedWage, 2),
            hours: fmt(nextHours, 1),
        }));
        updateExistingStaffDraft(employeeId, 'linkedProductiveEmployeeId', effectiveLinkedProdId);
        updateExistingStaffDraft(employeeId, 'isSplitDeduction', true);
        updateExistingStaffDraft(employeeId, 'base', clampedWage);
        updateExistingStaffDraft(employeeId, 'hours', nextHours);
    };

    const applySplitHoursWithSyncedWage = (targetHours: number) => {
        if (isLocked || isAustritt) return;
        commitDraftState();
        const prevWage = Math.max(0, Number(draft.inputs?.base) || 0);
        const prevHours = Math.max(0, Number(draft.inputs?.hours) || 0);
        if (!isSplitActive) {
            const freeHours = Math.max(0, Math.round(targetHours * 10) / 10);
            setLocalValues((prev: any) => ({ ...prev, hours: fmt(freeHours, 1), isSplitDeduction: false }));
            updateExistingStaffDraft(employeeId, 'isSplitDeduction', false);
            updateExistingStaffDraft(employeeId, 'hours', freeHours);
            return;
        }
        const { maxWage, maxHours, prodBaseWage, prodBaseHours } = getProdReference();
        const clampedHours = Math.round(Math.min(Math.max(0, targetHours), maxHours) * 10) / 10;
        let nextWage = Math.max(0, parse(String(localValues.base ?? 0)));
        if (prodBaseHours > 0 && prodBaseWage > 0) {
            const syncedWage = Math.round((prodBaseWage * (clampedHours / prodBaseHours)) * 100) / 100;
            nextWage = Math.min(Math.max(0, syncedWage), maxWage);
        }
        syncLinkedProdDraftByDelta(nextWage - prevWage, clampedHours - prevHours);
        setLocalValues((prev: any) => ({
            ...prev,
            linkedProductiveEmployeeId: effectiveLinkedProdId,
            isSplitDeduction: true,
            hours: fmt(clampedHours, 1),
            base: fmt(nextWage, 2),
        }));
        updateExistingStaffDraft(employeeId, 'linkedProductiveEmployeeId', effectiveLinkedProdId);
        updateExistingStaffDraft(employeeId, 'isSplitDeduction', true);
        updateExistingStaffDraft(employeeId, 'hours', clampedHours);
        updateExistingStaffDraft(employeeId, 'base', nextWage);
    };

    const hasOpenLinkedUnprodDraft = Object.entries(clientData.drafts?.existingStaff || {}).some(
        ([id, d]: [string, any]) =>
            id !== employeeId &&
            d?.inputs?.role === 'Unprod. Anteil' &&
            String(d?.inputs?.linkedProductiveEmployeeId || '') === String(employeeId)
    );
    const existingLinkedUnprods = findLinkedUnprodRows(
        String(employeeId),
        String(localValues.name || original.name || ''),
        clientData.unproductivePersonalData || []
    );
    const isProductiveRow =
        localValues.type === 'Produktiv (auch prod. Azubi)' ||
        (original as any)._sourceType === 'Produktiv (auch prod. Azubi)';
    const canAddUnprodAnteil =
        isProductiveRow &&
        !isAustritt &&
        !isLocked &&
        !hasOpenLinkedUnprodDraft &&
        existingLinkedUnprods.length === 0;

    const handleAddUnprodAnteil = () => {
        if (!canAddUnprodAnteil || !localValues.date) return;
        commitDraftState();
        const newId = generateSimpleId();
        seedExistingStaffDrafts([{
            id: newId,
            inputs: buildDraftInputs(localValues.date, {
                name: localValues.name || original.name || '',
                _sourceType: 'Unproduktiv',
                taetigkeit: 'Unprod. Anteil',
                grundgehalt: 0,
                lohnzusatz: 0,
                bonus: 0,
                wochenstunden: 0,
                arbeitstage: 0,
                linkedProductiveEmployeeId: employeeId,
                isSplitDeduction: true,
            }, {
                isNewUnprodAnteil: true,
                notes: 'Neuer Unprod. Anteil (per Änderungszentrale)',
            }),
        }]);
    };

    const isPairedRow = Boolean(pairRole);

    const renderStatus = () => {
        if (!draft.hasChanges) return <span className="status-badge-grey">UNVERÄNDERT</span>;
        if (isAustritt) return <span className="status-warn" style={{color: '#fca5a5', backgroundColor: 'rgba(153,27,27,0.2)', borderColor: '#7f1d1d'}}>AUSTRITT</span>;
        if (calcs.isMinWageIssue) return <span className="status-warn">PRÜFEN</span>;
        return <span className="status-ok">OK</span>;
    };

    const statusTooltip = !draft.hasChanges 
        ? "Keine Änderungen vorgenommen (wird nicht gesendet)." 
        : (isAustritt ? "Mitarbeiter wird zum Stichtag aus der Kalkulation entfernt." : (calcs.isMinWageIssue ? "Mindestlohn unterschritten!" : "Eingabe gültig."));

    // CSS Klasse für gesperrte / Paar-Zeilen
    const rowClass = [
        'pe-row active-row',
        isLocked ? 'locked-row' : '',
        pairRole === 'prod' ? 'pair-row-prod' : '',
        pairRole === 'unprod' ? 'pair-row-unprod' : '',
    ].filter(Boolean).join(' ');

    return (
        <tr className={rowClass}>
            {/* RESET */}
            <td className="col-tight" style={{ width: '1px' }}>
                <div className="pe-tooltip-wrapper" data-tooltip={isPairedRow ? 'Paar verwerfen (Produktiv + Unprod. Anteil)' : 'Änderung verwerfen / Reset'}>
                    <button className="reset-btn-row" onClick={() => resetStaffDraft(employeeId)} disabled={isLocked}>
                        <i className="fas fa-trash-alt" style={{ color: isLocked ? '#475569' : '#ef4444', fontSize: '0.8rem' }}></i>
                    </button>
                </div>
            </td>

            <td className="col-fixed-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Gültig ab (Monatsanfang)">
                    <select 
                        className="table-select field-date input-active-look" 
                        value={localValues.date} 
                        onChange={(e) => handleSelectChange('date', e.target.value)}
                        disabled={isLocked}
                    >
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            <td className="col-fixed-name">
                <div className="flex flex-col gap-1">
                    {pairRole === 'prod' && (
                        <div className="text-[10px] font-black uppercase tracking-wide text-emerald-400/90 px-1">
                            Produktiv · Anteil
                        </div>
                    )}
                    {(pairRole === 'unprod' || (isUnprodAnteilRow && !pairRole)) && (
                        <div className="text-[10px] font-black uppercase tracking-wide text-[#E6C299] px-1">
                            Unprod. Anteil{isNewUnprodAnteil ? ' · NEU' : ''}
                            {isSplitActive ? ' · gekoppelt' : ' · entkoppelt'}
                        </div>
                    )}
                    {isUnprodAnteilRow && !pairRole ? (
                        <div className="pe-tooltip-wrapper" data-tooltip="Produktiven Mitarbeiter auswählen, dessen unproduktiver Anteil angepasst wird">
                            <select
                                className="table-select active-value"
                                value={effectiveLinkedProdId}
                                disabled={isLocked || isAustritt}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const prodMa = productiveEmployees.find((p: any) => String(p.id) === String(selectedId));
                                    commitDraftState();
                                    const nextName = prodMa?.name ? String(prodMa.name) : localValues.name || '';
                                    setLocalValues((prev: any) => ({
                                        ...prev,
                                        name: nextName,
                                        linkedProductiveEmployeeId: selectedId || undefined,
                                        isSplitDeduction: Boolean(selectedId),
                                    }));
                                    updateExistingStaffDraft(employeeId, 'name', nextName);
                                    updateExistingStaffDraft(employeeId, 'linkedProductiveEmployeeId', selectedId || undefined);
                                    updateExistingStaffDraft(employeeId, 'isSplitDeduction', Boolean(selectedId));
                                }}
                                style={{ textAlign: 'left', paddingLeft: '0.5rem' }}
                            >
                                <option value="">-- Prod. Mitarbeiter wählen --</option>
                                {productiveEmployees.map((p: any) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div className="pe-tooltip-wrapper" data-tooltip="Mitarbeiter Name (Fix)">
                            <div className="input-wrapper">
                                <input type="text" className="table-input" value={localValues.name} disabled style={{textAlign:'left', paddingLeft:'0.5rem'}} />
                            </div>
                        </div>
                    )}
                    {canAddUnprodAnteil && (
                        <button
                            type="button"
                            onClick={handleAddUnprodAnteil}
                            className="mx-1 mt-0.5 rounded border border-[#E6C299]/40 bg-[#E6C299]/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-[#E6C299] hover:bg-[#E6C299]/20"
                            title="Unproduktiven Anteil für diesen Mitarbeiter anlegen"
                        >
                            + Unprod. Anteil
                        </button>
                    )}
                </div>
            </td>

            <td className="col-fixed-type">
                {(isUnprodAnteilRow || pairRole === 'unprod') && !isAustritt ? (
                    <div className="pe-tooltip-wrapper" data-tooltip="Unproduktiver Anteil — fest; Werte unten anpassen oder mit Abzug/Frei koppeln">
                        <div className="pair-type-badge pair-type-unprod">Unproduktiv</div>
                    </div>
                ) : pairRole === 'prod' && !isAustritt ? (
                    <div className="pe-tooltip-wrapper" data-tooltip="Produktiver Anteil — Umschalten auf Unproduktiv entfällt (zweite Zeile). Austritt weiterhin möglich.">
                        <select
                            className={`table-select field-type ${getClass('type', localValues.type, (original as any)._sourceType)}`}
                            value={localValues.type}
                            onChange={(e) => handleSelectChange('type', e.target.value)}
                            disabled={isLocked}
                        >
                            <option value="Produktiv (auch prod. Azubi)">Produktiv</option>
                            <option value="Austritt">Austritt / Kündigung</option>
                        </select>
                    </div>
                ) : (
                    <div className="pe-tooltip-wrapper" data-tooltip="Bereich ändern oder Austritt melden">
                        <select 
                            className={`table-select field-type ${getClass('type', localValues.type, (original as any)._sourceType || original.taetigkeit)} ${isAustritt ? 'austritt-type' : ''}`} 
                            value={localValues.type} 
                            onChange={(e) => handleSelectChange('type', e.target.value)}
                            disabled={isLocked}
                        >
                            <option value="Produktiv (auch prod. Azubi)">Produktiv</option>
                            <option value="Unproduktiv">Unproduktiv</option>
                            <option value="Azubi & Assistenz">Azubi/Assistenz</option>
                            <option value="Austritt">Austritt / Kündigung</option>
                        </select>
                    </div>
                )}
            </td>

            <td className="col-fixed-role">
                {(isUnprodAnteilRow || pairRole === 'unprod') && !isAustritt ? (
                    <div className="pe-tooltip-wrapper" data-tooltip="Unproduktiver Lohnanteil — Anpassung über Gehalt/Stunden und Abzug/Frei">
                        <div className="pair-type-badge pair-type-unprod">Unprod. Anteil</div>
                    </div>
                ) : (
                    <div className="pe-tooltip-wrapper" data-tooltip={roleTooltip}>
                        <select 
                            className={`table-select field-role ${getClass('role', localValues.role, original.taetigkeit)} ${isAustritt ? 'austritt-disabled' : ''}`} 
                            value={localValues.role} 
                            onChange={(e) => handleSelectChange('role', e.target.value)}
                            disabled={isLocked || isAustritt || pairRole === 'prod'}
                        >
                            {roleOptions.length === 0 && <option value="">Bitte wählen</option>}
                            {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                )}
            </td>

            <td className="col-fixed-num" style={hasLinkedProd ? { minWidth: '220px' } : undefined}>
                <div className="pe-tooltip-wrapper" data-tooltip={hasLinkedProd ? `Unprod. Anteil (${isSplitActive ? 'Abzug aktiv' : 'Frei'}) · Aktuell: ${fmt(original.grundgehalt)} ${currency}` : `Aktuell: ${fmt(ghostBase)} ${currency}${prodRestOrig ? ' (Rest nach Abzug)' : ''}`}>
                    <div className="flex items-center gap-1">
                        {hasLinkedProd && (
                            <div className="inline-flex shrink-0 items-center rounded border border-slate-700 overflow-hidden bg-slate-900">
                                <button
                                    type="button"
                                    disabled={isLocked || isAustritt}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleSplitCoupling(!isSplitActive)}
                                    title={isSplitActive ? 'Kopplung AKTIV (Abzug von Produktiv). Klicken für Frei.' : 'Kopplung AUS (Frei). Klicken für Abzug.'}
                                    className={`px-1.5 py-1 text-[9px] font-black uppercase leading-none border-r border-slate-700 transition-all ${
                                        isSplitActive ? 'bg-[#E6C299] text-black' : 'bg-slate-800 text-slate-400 hover:text-white'
                                    }`}
                                >
                                    {isSplitActive ? 'Abzug' : 'Frei'}
                                </button>
                                <button
                                    type="button"
                                    disabled={isLocked || isAustritt}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitWageWithSyncedHours(parse(String(localValues.base ?? 0)) - 100)}
                                    title="−100 € unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none text-slate-300 hover:bg-rose-500/20 hover:text-rose-400"
                                >
                                    −
                                </button>
                                <button
                                    type="button"
                                    disabled={isLocked || isAustritt}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitWageWithSyncedHours(parse(String(localValues.base ?? 0)) + 100)}
                                    title="+100 € unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none border-l border-slate-700 text-[#E6C299] hover:bg-emerald-500/20 hover:text-emerald-400"
                                >
                                    +
                                </button>
                            </div>
                        )}
                        <div className="input-wrapper flex-1">
                            <input 
                                className={`table-input ${getClass('base', localValues.base, ghostBase, true)} ${isAustritt ? 'austritt-disabled' : ''}`} 
                                value={localValues.base} 
                                onChange={(e)=>handleChange('base', e.target.value)} 
                                onBlur={()=>handleBlur('base')} 
                                onFocus={handleFocus} 
                                onKeyDown={handleKeyDown}
                                disabled={isLocked || isAustritt} 
                            />
                            <div className="unit-label"><span className="unit-text">{currency}</span></div>
                        </div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(original.lohnzusatz)} ${currency}`}>
                    <div className="input-wrapper">
                        <input 
                            className={`table-input ${getClass('add', localValues.add, original.lohnzusatz, true)} ${isAustritt ? 'austritt-disabled' : ''}`} 
                            value={localValues.add} 
                            onChange={(e)=>handleChange('add', e.target.value)} 
                            onBlur={()=>handleBlur('add')} 
                            onFocus={handleFocus} 
                            onKeyDown={handleKeyDown}
                            disabled={isLocked || isAustritt} 
                        />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(original.bonus)} ${currency}`}>
                    <div className="input-wrapper">
                        <input 
                            className={`table-input ${getClass('bonus', localValues.bonus, original.bonus, true)} ${isAustritt ? 'austritt-disabled' : ''}`} 
                            value={localValues.bonus} 
                            onChange={(e)=>handleChange('bonus', e.target.value)} 
                            onBlur={()=>handleBlur('bonus')} 
                            onFocus={handleFocus} 
                            onKeyDown={handleKeyDown} 
                            disabled={isLocked || isAustritt}
                        />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            
            {/* BRUTTO */}
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Gesamt Brutto (Automatisch)">
                    <div className="result-wrapper">
                        <div className={`table-calc ${isAustritt ? 'austritt-disabled' : ''}`}>{fmt(calcs.bruttoTotal)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>

            <td className="col-fixed-num" style={hasLinkedProd ? { minWidth: '185px' } : undefined}>
                <div className="pe-tooltip-wrapper" data-tooltip={`Aktuell: ${fmt(ghostHours, 1)} Std.${prodRestOrig ? ' (Rest nach Abzug)' : ''}`}>
                    <div className="flex items-center gap-1">
                        {hasLinkedProd && (
                            <div className="inline-flex shrink-0 items-center rounded border border-slate-700 overflow-hidden bg-slate-900">
                                <button
                                    type="button"
                                    disabled={isLocked || isAustritt}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitHoursWithSyncedWage(parse(String(localValues.hours ?? 0)) - 1)}
                                    title="−1 Std. unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none text-slate-300 hover:bg-rose-500/20 hover:text-rose-400"
                                >
                                    −
                                </button>
                                <button
                                    type="button"
                                    disabled={isLocked || isAustritt}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitHoursWithSyncedWage(parse(String(localValues.hours ?? 0)) + 1)}
                                    title="+1 Std. unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none border-l border-slate-700 text-[#E6C299] hover:bg-emerald-500/20 hover:text-emerald-400"
                                >
                                    +
                                </button>
                            </div>
                        )}
                        <div className="input-wrapper flex-1">
                            <input 
                                className={`table-input ${getClass('hours', localValues.hours, ghostHours, true)} ${isAustritt ? 'austritt-disabled' : ''}`} 
                                value={localValues.hours} 
                                onChange={(e)=>handleChange('hours', e.target.value)} 
                                onBlur={()=>handleBlur('hours')} 
                                onFocus={handleFocus} 
                                onKeyDown={handleKeyDown} 
                                disabled={isLocked || isAustritt}
                            />
                            <div className="unit-label"><span className="unit-text">Std.</span></div>
                        </div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip={isDaysDisabled ? "Nicht benötigt bei diesem Bereich" : `Aktuell: ${fmt(original.arbeitstage, 0)} Tage`}>
                    <div className="input-wrapper">
                        <input 
                            className={`table-input ${!isDaysDisabled ? getClass('days', localValues.days, original.arbeitstage, true) : ''} ${isAustritt ? 'austritt-disabled' : ''}`} 
                            value={isDaysDisabled && !isAustritt ? '0' : localValues.days} 
                            onChange={(e)=>handleChange('days', e.target.value)} 
                            onBlur={()=>handleBlur('days')} 
                            onFocus={handleFocus} 
                            onKeyDown={handleKeyDown}
                            disabled={isDaysDisabled || isLocked}
                            style={isDaysDisabled ? {color: '#475569', cursor: 'not-allowed'} : {}}
                        />
                        <div className="unit-label"><span className="unit-text">T.</span></div>
                    </div>
                </div>
            </td>

            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Stundenlohn (Basis)">
                    <div className="result-wrapper">
                        <div className={`table-calc ${isAustritt ? 'austritt-disabled' : ''}`}>{fmt(calcs.hourlyBase)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Stundenlohn (Effektiv inkl. Boni)">
                    <div className="result-wrapper">
                        <div className={`table-calc ${isAustritt ? 'austritt-disabled' : ''}`}>{fmt(calcs.hourlyTotal)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip={statusTooltip}>
                    <div className={`status-container field-status ${calcs.isMinWageIssue && !isAustritt ? 'warn-tooltip' : ''}`}>
                        {renderStatus()}
                    </div>
                </div>
            </td>

            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Lohnnebenkosten-Satz (Auto)">
                    <div className="result-wrapper">
                        <div className={`table-calc ${isAustritt ? 'austritt-disabled' : ''}`}>{fmt(calcs.svRate, 1)}</div>
                        <div className="unit-label"><span className="unit-text">%</span></div>
                    </div>
                </div>
            </td>
            
            <td className="col-expand">
                <div className="pe-tooltip-wrapper" data-tooltip={isAustritt ? "Grund oder Details zum Austritt..." : "Erklärung für den Coach"}>
                    <textarea 
                        className={`table-textarea ${localValues.notes ? 'active-value' : ''}`}
                        value={localValues.notes} 
                        onChange={(e)=>handleChange('notes', e.target.value)} 
                        onBlur={()=>handleBlur('notes')} 
                        placeholder={isAustritt ? "Hat gekündigt zum..." : "Notiz..."}
                        disabled={isLocked}
                    ></textarea>
                </div>
            </td>
            
            {/* ADMIN SPERRE */}
            <td className="col-tight">
                {isLocked ? (
                    <div className="admin-badge" onClick={() => onAdminLock(employeeId, true)}>
                        <span className="text-lg font-bold">{draft.lockedBy}</span>
                        <span style={{ fontSize: '0.55rem' }}>{draft.lockedAt}</span>
                    </div>
                ) : (
                    <button className="btn-admin-lock" onClick={() => onAdminLock(employeeId, false)}>
                        <i className="fas fa-lock"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 2. DIE JOKER-ZEILE (Eingang)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const JokerRow: React.FC = () => {
    const { clientData, seedExistingStaffDrafts, settings } = useAppStore();
    const currency = settings.country?.currency === 'CHF' ? 'CHF' : 'EUR';
    
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedEmpId, setSelectedEmpId] = useState('');

    const personalData = clientData.personalData || [];
    const unproductivePersonalData = clientData.unproductivePersonalData || [];

    const allEmployees = [
        ...personalData.map((e: any) => ({ ...e, _sourceType: 'Produktiv (auch prod. Azubi)' })),
        ...unproductivePersonalData.map((e: any) => ({ ...e, _sourceType: 'Unproduktiv' })),
        ...(clientData.azubiAssistenzData || []).map((e: any) => ({ ...e, _sourceType: 'Azubi & Assistenz' }))
    ].filter((e: any) => e && e.id);

    const existingDraftIds = new Set(Object.keys(clientData.drafts.existingStaff || {}));

    // Paar-IDs ausblenden, sobald eine Seite des Paares schon als Draft offen ist
    const blockedIds = new Set<string>(existingDraftIds);
    for (const draftId of existingDraftIds) {
        const d = clientData.drafts.existingStaff[draftId];
        const linked = String(d?.inputs?.linkedProductiveEmployeeId || '').trim();
        if (linked) blockedIds.add(linked);
        if (d?.inputs?.type === 'Produktiv (auch prod. Azubi)' || personalData.some((p: any) => String(p.id) === String(draftId))) {
            findLinkedUnprodRows(String(draftId), String(d?.inputs?.name || ''), unproductivePersonalData).forEach((u: any) => {
                if (u?.id) blockedIds.add(String(u.id));
            });
        }
        // Auch per Name verknüpfte Unprod.-Anteile blockieren
        if (d?.inputs?.role === 'Unprod. Anteil') {
            const pid = resolveLinkedProdId(
                { ...d.inputs, taetigkeit: 'Unprod. Anteil', linkedProductiveEmployeeId: d.inputs.linkedProductiveEmployeeId, name: d.inputs.name },
                personalData
            );
            if (pid) blockedIds.add(pid);
        }
    }

    const pairedProdIds = new Set<string>();
    for (const e of allEmployees) {
        if (!isUnprodAnteilEmp(e)) continue;
        const pid = resolveLinkedProdId(e, personalData);
        if (pid) pairedProdIds.add(pid);
    }

    const availableEmployees = allEmployees.filter((e: any) => {
        if (blockedIds.has(String(e.id))) return false;
        if (isUnprodAnteilEmp(e) && resolveLinkedProdId(e, personalData)) return false;
        return true;
    });

    const getAvailableLabel = (e: any) => {
        if (e._sourceType === 'Produktiv (auch prod. Azubi)' && pairedProdIds.has(String(e.id))) {
            return `${e.name || 'Ohne Name'} (Produktiv + Unprod. Anteil)`;
        }
        return getDropdownLabel(e);
    };

    const handleCommit = (empId: string) => {
        if (!selectedDate || !empId) return;
        
        const emp = allEmployees.find((e: any) => String(e.id) === String(empId));
        if (!emp) return;

        let items: Array<{ id: string; inputs: Record<string, any> }> = [];

        if (isUnprodAnteilEmp(emp)) {
            const prodId = resolveLinkedProdId(emp, personalData);
            const prod = prodId ? personalData.find((p: any) => String(p.id) === String(prodId)) : null;
            if (prod) {
                items = buildPairSeedItems(selectedDate, prod, unproductivePersonalData, existingDraftIds);
                // Falls der gewählte Unprod. noch nicht in linked war (nur Name), trotzdem anlegen
                if (!items.some((i) => i.id === String(emp.id)) && !existingDraftIds.has(String(emp.id))) {
                    items.push({
                        id: String(emp.id),
                        inputs: buildDraftInputs(selectedDate, emp, {
                            linkedProductiveEmployeeId: String(prod.id),
                            isSplitDeduction: emp.isSplitDeduction !== false,
                        }),
                    });
                }
            } else if (!existingDraftIds.has(String(emp.id))) {
                items = [{
                    id: String(emp.id),
                    inputs: buildDraftInputs(selectedDate, emp, {
                        isSplitDeduction: emp.isSplitDeduction !== false,
                    }),
                }];
            }
        } else if (emp._sourceType === 'Produktiv (auch prod. Azubi)') {
            items = buildPairSeedItems(selectedDate, emp, unproductivePersonalData, existingDraftIds);
        } else if (!existingDraftIds.has(String(emp.id))) {
            items = [{ id: String(emp.id), inputs: buildDraftInputs(selectedDate, emp) }];
        }

        if (items.length > 0) seedExistingStaffDrafts(items);

        setSelectedEmpId('');
        setSelectedDate('');
    };

    if (availableEmployees.length === 0) return null;

    return (
        <tr className="pe-row">
            {/* RESET - FIX: MÜLLEIMER (INAKTIV/GRAU) STATT PLUS */}
            <td className="col-tight">
                <button className="reset-btn-row" disabled>
                    <i className="fas fa-trash-alt text-stone-300 dark:text-slate-800"></i>
                </button>
            </td>

            {/* 1. DATUM */}
            <td className="col-fixed-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Schritt 1: Datum wählen">
                    <select 
                        className={`table-select field-date ${!selectedDate ? 'border-blue-500/50' : ''}`} 
                        value={selectedDate} 
                        onChange={(e)=>setSelectedDate(e.target.value)}
                    >
                        <option value="">Bitte Wählen</option>
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* 2. NAME */}
            <td className="col-fixed-name">
                <div className="pe-tooltip-wrapper" data-tooltip="Schritt 2: Mitarbeiter wählen — Paare (Produktiv + Unprod. Anteil) werden gemeinsam geladen">
                    <select 
                        className="table-select field-name" 
                        disabled={!selectedDate} 
                        value={selectedEmpId} 
                        onChange={(e) => handleCommit(e.target.value)}
                        style={{ textAlign: 'left', paddingLeft: '0.5rem', color: !selectedDate ? '#475569' : '#E6C299' }}
                    >
                        <option value="">-- Mitarbeiter auswählen --</option>
                        {availableEmployees.map((e: any) => (
                            <option key={e.id} value={e.id}>{getAvailableLabel(e)}</option>
                        ))}
                    </select>
                </div>
            </td>

            {/* GHOST VALUES */}
            <td className="col-fixed-type"><select className="table-select ghost-value" disabled><option>Bitte wählen</option></select></td> 
            <td className="col-fixed-role"><select className="table-select ghost-value" disabled><option>Bitte wählen</option></select></td> 
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="Grundg." /><div className="unit-label"><span className="unit-text">{currency}</span></div></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="Zusatz" /><div className="unit-label"><span className="unit-text">{currency}</span></div></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="Bonus" /><div className="unit-label"><span className="unit-text">{currency}</span></div></div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">0,00</div><div className="unit-label"><span className="unit-text">{currency}</span></div></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="Std." /><div className="unit-label"><span className="unit-text">Std.</span></div></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="T." /><div className="unit-label"><span className="unit-text">T.</span></div></div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">0,00</div><div className="unit-label"><span className="unit-text">{currency}</span></div></div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">0,00</div><div className="unit-label"><span className="unit-text">{currency}</span></div></div></td>
            <td className="col-fixed-num"><div className="status-container field-status status-neutral">--</div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">—</div><div className="unit-label"><span className="unit-text">%</span></div></div></td>
            <td className="col-expand"><textarea className="table-textarea" disabled placeholder="Notizen..."></textarea></td>
            <td><button className="btn-admin-lock" disabled><i className="fas fa-lock"></i></button></td>
        </tr>
    );
};


// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// HAUPT-KOMPONENTE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ExistingStaffTableProps {
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ExistingStaffTable: React.FC<ExistingStaffTableProps> = ({ onAdminLock }) => {
    const { clientData, seedExistingStaffDrafts } = useAppStore();
    const drafts = clientData.drafts?.existingStaff || {};
    const personalData = clientData.personalData || [];
    const unproductivePersonalData = clientData.unproductivePersonalData || [];

    // Unvollständige Paare automatisch ergänzen (z.B. nur Unprod. offen → Produktiv nachladen)
    useEffect(() => {
        const draftIds = new Set(Object.keys(drafts));
        const healItems: Array<{ id: string; inputs: Record<string, any> }> = [];

        for (const [id, d] of Object.entries(drafts)) {
            const inputs = (d as any)?.inputs || {};
            const date = inputs.date || '01.01.';

            // Unprod. offen ohne Produktiv-Draft
            if (inputs.role === 'Unprod. Anteil') {
                const prodId = resolveLinkedProdId(
                    {
                        taetigkeit: 'Unprod. Anteil',
                        linkedProductiveEmployeeId: inputs.linkedProductiveEmployeeId,
                        name: inputs.name,
                    },
                    personalData
                );
                if (prodId && !draftIds.has(prodId)) {
                    const prod = personalData.find((p: any) => String(p.id) === String(prodId));
                    if (prod) {
                        const pairItems = buildPairSeedItems(date, prod, unproductivePersonalData, draftIds);
                        for (const item of pairItems) {
                            if (!draftIds.has(item.id) && !healItems.some((h) => h.id === item.id)) {
                                healItems.push(item);
                                draftIds.add(item.id);
                            }
                        }
                    }
                }
            }

            // Produktiv offen ohne verknüpfte Unprod.-Drafts
            const isProdDraft =
                inputs.type === 'Produktiv (auch prod. Azubi)' ||
                personalData.some((p: any) => String(p.id) === String(id));
            if (isProdDraft && inputs.role !== 'Unprod. Anteil') {
                const prod = personalData.find((p: any) => String(p.id) === String(id));
                if (prod) {
                    const pairItems = buildPairSeedItems(date, prod, unproductivePersonalData, draftIds);
                    for (const item of pairItems) {
                        if (!draftIds.has(item.id) && !healItems.some((h) => h.id === item.id)) {
                            healItems.push(item);
                            draftIds.add(item.id);
                        }
                    }
                }
            }
        }

        if (healItems.length > 0) {
            seedExistingStaffDrafts(healItems);
        }
        // Nur bei Draft-/Stammdaten-Wechsel heilen
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Object.keys(drafts).sort().join('|'), personalData.length, unproductivePersonalData.length]);

    // Paare untereinander: Produktiv zuerst, dann Unprod.-Anteile
    const { orderedIds, pairRoleById } = useMemo(() => {
        const ids = Object.keys(drafts);
        const pairRoleById: Record<string, 'prod' | 'unprod'> = {};
        const ordered: string[] = [];
        const used = new Set<string>();

        const resolveProdForUnprodDraft = (d: any) => {
            if (d?.inputs?.role !== 'Unprod. Anteil') return '';
            return resolveLinkedProdId(
                {
                    taetigkeit: 'Unprod. Anteil',
                    linkedProductiveEmployeeId: d.inputs.linkedProductiveEmployeeId,
                    name: d.inputs.name,
                },
                personalData
            );
        };

        for (const id of ids) {
            if (used.has(id)) continue;
            const d = drafts[id];
            const linkedProd = resolveProdForUnprodDraft(d);
            if (linkedProd) continue; // unter Produktiv einordnen

            ordered.push(id);
            used.add(id);

            const childUnprods = ids.filter((uid) => {
                if (used.has(uid)) return false;
                return resolveProdForUnprodDraft(drafts[uid]) === String(id);
            });

            if (childUnprods.length > 0) {
                pairRoleById[id] = 'prod';
                for (const uid of childUnprods) {
                    ordered.push(uid);
                    used.add(uid);
                    pairRoleById[uid] = 'unprod';
                }
            }
        }

        for (const id of ids) {
            if (used.has(id)) continue;
            ordered.push(id);
            used.add(id);
            const linkedProd = resolveProdForUnprodDraft(drafts[id]);
            if (linkedProd) pairRoleById[id] = 'unprod';
        }

        return { orderedIds: ordered, pairRoleById };
    }, [drafts, personalData]);

    return (
        <div className="table-wrapper">
            <TableStyles />
            <table className="text-left border-collapse">
                <thead className="pe-table-header">
                    <tr>
                        <th className="col-tight px-2 min-w-[50px]">Reset</th>
                        <th className="col-fixed-date px-2">Gültig ab</th>
                        <th className="col-fixed-name px-2">Name</th>
                        <th className="col-fixed-type px-2">Bereich/Typ</th>
                        <th className="col-fixed-role px-2">Tätigkeit</th>
                        <th className="col-fixed-num px-2">Grundgehalt</th>
                        <th className="col-fixed-num px-2">Lohnzusatz</th>
                        <th className="col-fixed-num px-2">Bonus</th>
                        <th className="col-fixed-num px-2">Bruttolohn</th>
                        <th className="col-fixed-num px-2">Std./Wo</th>
                        <th className="col-fixed-num px-2">Tage/Wo</th>
                        <th className="col-fixed-num px-2">Std. Lohn (G)</th>
                        <th className="col-fixed-num px-2">Std. Lohn (Ges)</th>
                        <th className="col-fixed-num px-2">Mindestlohn</th>
                        <th className="col-fixed-num px-2">SV-Satz</th>
                        <th className="col-expand px-2 min-w-[400px]">Info / Notizen</th>
                        <th className="col-tight px-2 min-w-[80px]">Admin</th>
                    </tr>
                </thead>
                <tbody id="existingStaffBody">
                    {orderedIds.map(id => (
                        <ActiveStaffRow
                            key={id}
                            employeeId={id}
                            onAdminLock={onAdminLock}
                            pairRole={pairRoleById[id] || null}
                        />
                    ))}
                    <JokerRow />
                </tbody>
            </table>
        </div>
    );
};

export default ExistingStaffTable;